import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import {
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  BookOpen,
  Users,
  LogOut,
  User,
  MapPin,
  Clock,
  Loader2,
  AlertCircle,
  ExternalLink,
  Link2,
  RefreshCw,
  History,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SidebarBrand, SwitchAppList } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { toast } from "react-hot-toast";

// ─── constants ────────────────────────────────────────────────────────────────

const DAYS_SHORT  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS      = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun",
                      "Jul","Aug","Sep","Oct","Nov","Dec"];

// ─── helpers ─────────────────────────────────────────────────────────────────

function getInitials(name) {
  return name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";
}

function getCalendarCells(year, month) {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ day: daysInPrev - i, current: false });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, current: true });
  const rem = cells.length % 7;
  if (rem !== 0)
    for (let d = 1; d <= 7 - rem; d++)
      cells.push({ day: d, current: false });
  return cells;
}

/** Parse a PCO event item and return a normalised event object. */
function normaliseEvent(item, source) {
  const a = item?.attributes ?? {};

  // event_instances have starts_at / ends_at at the instance level
  // events have starts_at / ends_at on the event itself
  const startsAt = a.starts_at || a.start || null;
  const endsAt   = a.ends_at   || a.end   || null;
  const name     = a.name || a.summary || "Untitled Event";

  return {
    id:          item.id,
    name,
    startsAt,
    endsAt,
    location:    a.location_name || a.location || null,
    description: a.description || null,
    allDay:      !!a.all_day_event,
    source,
  };
}

function formatTime(isoStr, allDay) {
  if (!isoStr) return null;
  if (allDay) return "All day";
  try {
    return new Date(isoStr).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return null; }
}

function formatDateShort(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
  } catch { return ""; }
}

/** Return "YYYY-MM-DD" for a given Date (local). */
function toLocalDateKey(isoStr) {
  if (!isoStr) return null;
  try {
    const d = new Date(isoStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch { return null; }
}

// ─── sidebar nav item ─────────────────────────────────────────────────────────

function NavItem({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left text-sm transition-all
        ${active ? "bg-amber-50 text-amber-700 font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
    >
      <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-amber-600" : "text-slate-400"}`} />
      <span className="hidden xl:block truncate">{label}</span>
      {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-amber-500 rounded-r-full" />}
    </button>
  );
}

// ─── event card ──────────────────────────────────────────────────────────────

function EventCard({ event, compact = false }) {
  const time = formatTime(event.startsAt, event.allDay);
  const date = formatDateShort(event.startsAt);

  if (compact) {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg hover:bg-amber-50 transition-colors group">
        <div className="w-1 h-full min-h-[32px] rounded-full bg-amber-400 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-800 truncate leading-tight">{event.name}</p>
          {time && <p className="text-[11px] text-amber-600 mt-0.5">{time}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 p-3 rounded-xl border border-slate-100 hover:border-amber-200 hover:bg-amber-50/40 transition-all">
      {/* Date column */}
      <div className="shrink-0 w-11 text-center">
        <p className="text-[10px] font-bold uppercase tracking-wide text-amber-500">{date.split(" ")[0]}</p>
        <p className="text-xl font-bold text-slate-800 leading-tight">{date.split(" ")[1]}</p>
      </div>
      <div className="w-px bg-slate-200 shrink-0" />
      {/* Details */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 leading-snug">{event.name}</p>
        <div className="mt-1 flex flex-col gap-0.5">
          {time && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock className="w-3 h-3 text-amber-400 shrink-0" />
              {time}
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>
        {event.description && (
          <p className="mt-1.5 text-xs text-slate-400 line-clamp-2 leading-relaxed">
            {event.description}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function Events({ user }) {
  const navigate    = useNavigate();
  const today       = new Date();

  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(today.getDate());

  const [pcoStatus,  setPcoStatus]  = useState(null);   // null=loading, {connected}
  const [events,     setEvents]     = useState([]);      // all events for current month
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  // Events is temporarily admin-only — redirect regular accounts away
  useEffect(() => {
    if (user && !user.is_super_admin) {
      navigate("/home");
    }
  }, [user, navigate]);

  // ── PCO connection check ──────────────────────────────────────────────────

  useEffect(() => {
    axios.get(`${API}/pco/status`)
      .then(r => setPcoStatus(r.data))
      .catch(() => setPcoStatus({ connected: false }));
  }, []);

  // ── Fetch events for current month ───────────────────────────────────────

  const fetchEvents = useCallback(async (year, month) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/pco/calendar`, {
        params: { year, month: month + 1, per_page: 100 },
      });
      const raw   = res.data?.data ?? [];
      const source = res.data?._source ?? "events";
      const normalised = raw
        .map(item => normaliseEvent(item, source))
        .filter(e => e.startsAt)           // drop events with no date
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
      setEvents(normalised);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPcoStatus({ connected: false });
      } else {
        setError("Failed to load events from Planning Center.");
      }
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pcoStatus?.connected) {
      fetchEvents(viewYear, viewMonth);
    }
  }, [pcoStatus, viewYear, viewMonth, fetchEvents]);

  // ── Derived data ──────────────────────────────────────────────────────────

  // Map date-key → events[]
  const eventsByDay = events.reduce((acc, ev) => {
    const key = toLocalDateKey(ev.startsAt);
    if (key) {
      acc[key] = acc[key] ? [...acc[key], ev] : [ev];
    }
    return acc;
  }, {});

  const makeDayKey = (day) =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  // Events for the selected day
  const selectedKey    = selectedDay ? makeDayKey(selectedDay) : null;
  const selectedEvents = selectedKey ? (eventsByDay[selectedKey] ?? []) : [];

  // Upcoming events: today onwards in the current month
  const upcomingEvents = events.filter(ev => {
    if (!ev.startsAt) return false;
    const d = new Date(ev.startsAt);
    return d >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }).slice(0, 10);

  // Calendar cells
  const cells = getCalendarCells(viewYear, viewMonth);

  const isToday = (day, current) =>
    current &&
    day === today.getDate() &&
    viewMonth === today.getMonth() &&
    viewYear === today.getFullYear();

  // ── Month navigation ──────────────────────────────────────────────────────

  const prevMonth = () => {
    setSelectedDay(null);
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    setSelectedDay(null);
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDay(today.getDate());
  };

  // ── Auth ──────────────────────────────────────────────────────────────────

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } finally { navigate("/"); }
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (user && !user.is_super_admin) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex md:ml-[68px] xl:ml-[260px] justify-center">

        {/* ═══ LEFT SIDEBAR ═══ */}
        <aside className="hidden md:flex flex-col fixed top-0 left-0 h-screen w-[68px] xl:w-[260px] shrink-0 bg-white border-r border-slate-200 px-2 xl:px-4 py-4 overflow-y-auto z-40">

          {/* App Switcher */}
          <div className="h-16 shrink-0 flex items-center px-1 mb-1">
            <SidebarBrand brandLabel="Events" />
          </div>

          <SwitchAppList user={user} active="events" />

          <Separator className="mb-2" />

          <p className="px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hidden xl:block">
            Current App
          </p>

          <nav className="flex flex-col gap-0.5 flex-1 relative">
            <NavItem icon={CalendarDays} label="Calendar" active onClick={() => {}} />
          </nav>

          <Separator className="my-3" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger"
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors w-full">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 text-left hidden xl:block">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-52" side="top" align="start">
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <User className="w-4 h-4 mr-2" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="sidebar-versions-link" onClick={() => navigate("/versions")}>
                <History className="w-4 h-4 mr-2" /> Versions
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </aside>

        {/* ═══ MAIN CONTENT ═══ */}
        <main className="w-full max-w-5xl px-4 sm:px-6 py-8">

          {/* Page header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Church Events</h1>
              <p className="text-sm text-slate-500 mt-0.5">Upcoming services, gatherings, and special events</p>
            </div>
            <div className="flex items-center gap-2">
              {pcoStatus?.connected && (
                <Button variant="ghost" size="sm"
                  data-testid="events-refresh-btn"
                  onClick={() => fetchEvents(viewYear, viewMonth)}
                  disabled={loading}
                  className="h-8 px-2 text-slate-400 hover:text-amber-600">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
              )}
              {pcoStatus?.connected ? (
                <Badge className="bg-green-100 text-green-700 border-green-200 gap-1.5 px-2 py-1 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                  Planning Center
                </Badge>
              ) : pcoStatus !== null && (
                <button
                  data-testid="events-connect-pco-header-btn"
                  onClick={() => navigate("/planning-center")}
                  className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 transition-colors">
                  <Link2 className="w-3.5 h-3.5" />
                  Connect Planning Center
                </button>
              )}
            </div>
          </div>

          {/* Not connected banner */}
          {pcoStatus !== null && !pcoStatus.connected && (
            <div className="mb-6 flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
              <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Planning Center not connected</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Connect your Planning Center account to display live church events on this calendar.{" "}
                  <button onClick={() => navigate("/planning-center")}
                    className="underline font-semibold hover:text-amber-700">
                    Connect now
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="mb-6 flex items-center gap-3 p-4 rounded-xl border border-red-200 bg-red-50">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Calendar Grid ───────────────────────────────── */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">

                {/* Month nav */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                  <h2 className="text-base font-semibold text-slate-900">
                    {MONTHS[viewMonth]} {viewYear}
                    {loading && <Loader2 className="inline w-3.5 h-3.5 ml-2 animate-spin text-amber-400" />}
                  </h2>
                  <div className="flex items-center gap-1">
                    <Button data-testid="calendar-prev-month" variant="ghost" size="icon"
                      className="h-8 w-8 rounded-lg hover:bg-slate-100" onClick={prevMonth}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm"
                      className="h-8 px-3 text-xs font-medium hover:bg-slate-100" onClick={goToday}>
                      Today
                    </Button>
                    <Button data-testid="calendar-next-month" variant="ghost" size="icon"
                      className="h-8 w-8 rounded-lg hover:bg-slate-100" onClick={nextMonth}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Day-of-week header */}
                <div className="grid grid-cols-7 border-b border-slate-100">
                  {DAYS_SHORT.map(d => (
                    <div key={d} className="py-2 text-center text-xs font-semibold text-slate-400 uppercase tracking-wide">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7">
                  {cells.map((cell, idx) => {
                    const todayCell   = isToday(cell.day, cell.current);
                    const isSelected  = cell.current && cell.day === selectedDay;
                    const dayKey      = cell.current ? makeDayKey(cell.day) : null;
                    const dayEvents   = dayKey ? (eventsByDay[dayKey] ?? []) : [];
                    const hasEvents   = dayEvents.length > 0;

                    return (
                      <button
                        key={idx}
                        data-testid={cell.current ? `calendar-day-${cell.day}` : undefined}
                        onClick={() => cell.current && setSelectedDay(cell.day)}
                        className={`
                          relative min-h-[72px] p-1.5 text-left border-b border-r border-slate-100 transition-colors
                          ${!cell.current ? "bg-slate-50/50 cursor-default" : "hover:bg-amber-50/50 cursor-pointer"}
                          ${isSelected && !todayCell ? "bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}
                        `}
                      >
                        {/* Date number */}
                        <span className={`
                          inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium
                          ${todayCell
                            ? "bg-amber-500 text-white"
                            : cell.current
                              ? "text-slate-800"
                              : "text-slate-300"}
                        `}>
                          {cell.day}
                        </span>

                        {/* Event dots / labels */}
                        {hasEvents && (
                          <div className="mt-0.5 flex flex-col gap-0.5 overflow-hidden max-h-[36px]">
                            {dayEvents.slice(0, 2).map(ev => (
                              <span key={ev.id}
                                className="text-[10px] leading-tight font-medium text-amber-700 bg-amber-100 rounded px-1 truncate block">
                                {ev.name}
                              </span>
                            ))}
                            {dayEvents.length > 2 && (
                              <span className="text-[10px] text-slate-400 px-1">+{dayEvents.length - 2} more</span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Right Panel ─────────────────────────────────── */}
            <div className="lg:col-span-1 flex flex-col gap-4">

              {/* Selected day events */}
              {selectedDay && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 border-b border-slate-100 bg-amber-50">
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">
                      {MONTHS[viewMonth]} {selectedDay}
                    </p>
                    <p className="text-sm font-bold text-slate-800 mt-0.5">
                      {selectedEvents.length > 0
                        ? `${selectedEvents.length} event${selectedEvents.length > 1 ? "s" : ""}`
                        : "No events"}
                    </p>
                  </div>
                  <div className="p-2">
                    {selectedEvents.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-4">Nothing scheduled.</p>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {selectedEvents.map(ev => (
                          <EventCard key={ev.id} event={ev} compact />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Upcoming events */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm flex-1">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Upcoming Events</h3>
                  {events.length > 0 && (
                    <Badge variant="secondary" className="text-xs">{upcomingEvents.length}</Badge>
                  )}
                </div>

                {/* Loading */}
                {loading && (
                  <div className="flex justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                  </div>
                )}

                {/* Not connected */}
                {!loading && !pcoStatus?.connected && (
                  <div className="flex flex-col items-center py-10 px-4 text-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-amber-400" />
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Connect Planning Center to display upcoming church events.
                    </p>
                    <button data-testid="events-connect-pco-btn"
                      onClick={() => navigate("/planning-center")}
                      className="text-xs text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2">
                      Connect now
                    </button>
                  </div>
                )}

                {/* Connected + no events */}
                {!loading && pcoStatus?.connected && upcomingEvents.length === 0 && !error && (
                  <div className="flex flex-col items-center py-10 px-4 text-center gap-2">
                    <CalendarDays className="w-6 h-6 text-slate-300" />
                    <p className="text-xs text-slate-400">No upcoming events this month.</p>
                  </div>
                )}

                {/* Event list */}
                {!loading && upcomingEvents.length > 0 && (
                  <div className="p-3 flex flex-col gap-2 overflow-y-auto max-h-[420px]">
                    {upcomingEvents.map(ev => (
                      <EventCard key={ev.id} event={ev} />
                    ))}
                  </div>
                )}
              </div>

              {/* PCO badge */}
              {pcoStatus?.connected && (
                <div className="flex items-center gap-2 text-xs text-slate-400 justify-center">
                  <ExternalLink className="w-3 h-3" />
                  <span>Powered by Planning Center</span>
                </div>
              )}

              {/* Not connected — amber info card */}
              {!pcoStatus?.connected && (
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-80 mb-2">Live Church</p>
                  <p className="text-sm font-medium leading-relaxed">
                    Sunday services and weekly gatherings will appear here once Planning Center is connected.
                  </p>
                  <div className="mt-3 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 text-xs opacity-90">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>Service times coming soon</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs opacity-90">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      <span>Location details coming soon</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_disciple-hub-3/artifacts/wqhxldc5_Live%20Church%20Logo%20Print.png"
            alt="Live Church" className="h-7 w-auto"
          />
          <span className="font-serif font-bold text-sm text-slate-900">Events</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/home")} className="text-xs">
          Switch App
        </Button>
      </header>
    </div>
  );
}
